import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { endOfDay, isAfter, isBefore, parseISO, startOfDay } from 'date-fns';

import { Box, Loader, styled, Table, themeGet } from '@qwealth/qcore';
import { IUser, selectUser, selectUserIsAdmin, selectUserIsAdvisor, selectUserIsAdvisorSupport, selectUserIsCompliance } from '@qwealth/qdata';

import { selectClientsComplianceReview, selectComplianceAssignees, selectComplianceIsLoading } from 'data/selectors/compliance';
import { useAppSelector, useAppDispatch } from 'data/store';
import { dateRangeValidator } from 'data/helpers/dateTimeHelper';
import { ClientComplianceReview, ClientComplianceReviewFilters, ClientComplianceReviewQueryParams } from 'data/models/Compliance';
import { useDebounce } from 'hooks/useDebounce';
import { createColumns, filteredClientsToReview, getSavedFilters, saveFiltersInLocalStorage } from 'data/helpers/compliance';
import ComplianceReviewsHeader from './Header/ComplianceReviewsHeader';
import { getClientsComplianceReview, getComplianceAssignees, resetComplianceData } from 'data/actions/compliance';

export const StyledTable = styled(Box)`
  thead {
    width: 100% !important;
    background-color: ${themeGet('colors.gray.3')};
    th {
      color: ${themeGet('colors.dark')} !important;
      text-transform: capitalize !important;
      font-size: ${themeGet('fontSizes.default')} !important;
    }
  }
  tbody {
    tr:hover {
      background: ${themeGet('colors.gray.3')} !important;
    }
  }
`;

const ComplianceReviewsPage: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedHouseholdQID = useParams().householdQID;

  const clientsToReview = useAppSelector(selectClientsComplianceReview);
  const complianceAssignees = useAppSelector(selectComplianceAssignees);
  const isUserAdmin = useAppSelector(selectUserIsAdmin);
  const isUserCompliance = useAppSelector(selectUserIsCompliance);
  const isUserAdvisor = useAppSelector(selectUserIsAdvisor);
  const isUserAdvisorSupport = useAppSelector(selectUserIsAdvisorSupport);
  const user: IUser = useAppSelector(selectUser);
  const isLoading = useAppSelector(selectComplianceIsLoading);
  const hasPermissionToChangeAssignee = isUserAdmin || isUserCompliance;

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredClients, setFilteredClients] = useState<ClientComplianceReview[]>(clientsToReview);
  const [mutableFilters, setMutableFilters] = useState<ClientComplianceReviewFilters>(getSavedFilters());

  const ipsDateError = dateRangeValidator(mutableFilters.ipsDateStart, mutableFilters.ipsDateEnd);
  const query = useDebounce(searchQuery.toLowerCase(), 500);
  const countFilteredClients = filteredClientsToReview(filteredClients, query).length;

  const rowEvents = {
    onClick: (e, row): void => {
      const QID = row.QID;
      const householdQID = row.household.QID;
      isUserAdmin || isUserCompliance
        ? navigate(`/compliance/contact/${QID}?section=Messages`)
        : navigate(`/household?household=${householdQID}&householdTab=compliance`);
    },
  };

  const noDataIndication = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Loader />
        </Box>
      );
    } else {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" fontSize="default">
          No records found.
        </Box>
      );
    }
  };

  useEffect(() => {
    if (selectedHouseholdQID) {
      setSearchQuery(selectedHouseholdQID);
    }
  }, [selectedHouseholdQID]);

  useEffect(() => {
    let queryParams: ClientComplianceReviewQueryParams | undefined;
    if (isUserAdmin || isUserCompliance) {
      queryParams = undefined;
    } else if (isUserAdvisor || isUserAdvisorSupport) {
      queryParams = { partnerId: user.partnerID };
    } else {
      queryParams = { iaCode: user.iaCodes.toString() };
    }
    (async () => {
      await dispatch(getClientsComplianceReview(queryParams));
      await dispatch(getComplianceAssignees());
    })();
  }, [isUserAdmin, isUserCompliance, isUserAdvisor, isUserAdvisorSupport, dispatch]);

  useEffect(() => {
    const filterClients: ClientComplianceReview[] = clientsToReview.filter(client => {
      if (mutableFilters.assignee && client.assignee !== mutableFilters.assignee) {
        return false;
      }

      if (mutableFilters.partnerName && client.partnerName !== mutableFilters.partnerName) {
        return false;
      }

      if (mutableFilters.complianceChecklistStatus && client.complianceChecklistStatus !== mutableFilters.complianceChecklistStatus) {
        return false;
      }

      if (mutableFilters.responsibleComplianceActors) {
        if (!client.responsibleComplianceActors) {
          return false;
        } else if (!client.responsibleComplianceActors.includes(mutableFilters.responsibleComplianceActors)) {
          return false;
        }
      }

      if (mutableFilters.ipsDateStart && mutableFilters.ipsDateEnd && !ipsDateError) {
        if (client.ipsAcceptanceDate) {
          const ipsAcceptanceDateToCompare = parseISO(client.ipsAcceptanceDate);
          const ipsDateStart = parseISO(mutableFilters.ipsDateStart);
          const ipsDateEnd = parseISO(mutableFilters.ipsDateEnd);
          const isIpsAcceptanceDateInRange =
            isAfter(ipsAcceptanceDateToCompare, startOfDay(ipsDateStart)) && isBefore(ipsAcceptanceDateToCompare, endOfDay(ipsDateEnd));
          if (!isIpsAcceptanceDateInRange) {
            return false;
          }
        } else {
          return false;
        }
      }

      return true;
    });
    setFilteredClients(filterClients);
    saveFiltersInLocalStorage(mutableFilters);
  }, [mutableFilters, clientsToReview]);

  return (
    <>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Loader />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" backgroundColor="white" padding="large" boxShadow="layout" overflowY="hidden">
          <ComplianceReviewsHeader
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            mutableFilters={mutableFilters}
            setMutableFilters={setMutableFilters}
          />
          <StyledTable>
            <Table
              // @ts-ignore
              keyField="QID"
              data={filteredClientsToReview(filteredClients, query)}
              columns={createColumns(dispatch, complianceAssignees, hasPermissionToChangeAssignee)}
              fixedHeader
              striped
              bordered={false}
              linkableRow
              rowEvents={rowEvents}
              tableHeight="700px"
              noDataIndication={noDataIndication}
            />
            <Box fontWeight="bold" mt="large" fontStyle="italic">
              Showing {countFilteredClients} results
            </Box>
          </StyledTable>
        </Box>
      )}
    </>
  );
};

export default ComplianceReviewsPage;
